import { initAiIdExchange } from './aiIdExchange.js';

class PhoneSwapping {
	constructor() {
		if (!window?.DDC?.siteSettings || !window?.DDC?.callTrackingSettings) {
			// eslint-disable-next-line no-console
			console.warn(
				'Skipping phone swapping: missing window.DDC.siteSettings.callTrackingSettings.'
			);
			return;
		}
		this.DEPT_WILD_CARD = '*';
		this.cleanRegex = /[()\s.+-]/gi;
		this.phoneMetaParamRegex = /(?:dept=)([^&]*)&(?:accountId=)([^&]*)/;
		this.defaultCleanCapturingRegEx = /^(\d{3})(\d{3})(\d{4})$/;
		this.defaultReplacementFormat = '$1-$2-$3';
		this.parensReplacementFormat = '($1) $2-$3';
		this.dotReplacementFormat = '$1.$2.$3';
		// eslint-disable-next-line max-len
		this.compiledPhoneRegEx =
			/((?:(?:\+){1}?(?:[\s-.]{1})?)?(?:(?:\d{7,11})|(?:\(?:?(?:\d{3})\)?(?:[\s-.])?(?:\d{3})(?:[\s-.])?(?:\d{4}))))(?:\?([^\s]*))?/gm;
		this.compiledLinkPhoneRegex =
			/(?:tel:)(?:\d{1})?((?:\d{3})(?:[\s-.])?(?:\d{3})(?:[\s-.])?(?:\d{4}))(?:\?([^\s]*))?/;
		// Check URL params for values before getting from cookie
		this.urlParams = new URLSearchParams(window.location.search);
		this.ddcref =
			this.urlParams.get('ddcref') || sessionStorage.getItem('ddcref');
		this.tcdcmpid =
			this.urlParams.get('tcdcmpid') || sessionStorage.getItem('tcdcmpid');
		this.artifactOverride =
			this.urlParams.get('artifactOverride') ||
			this.getCookieValue('phoneSwappingArtifact');

		this.root = document.body;
		this.head = document.head;
		this.newrelic = window.newrelic;
		this.excludedAttributes = ['vin', 'src', 'uuid', 'fragmentId', 'slideId'];
		/**
		 * @type { AccountInfo[] }
		 */
		this.allAccounts = [];

		// used to limit amount of times we wait for request to finish
		this.waitCount = 0;
		this.waitLimit = 15;

		this.waitCountAutoId = 0;
		this.waitLimitAutoId = 30;
		// used to limit the amount of times we retry accounts that respond in error
		this.errorCount = {};
		this.errorLimit = 3;
		// store numbers that have already been looked up to prevent duplicating the lookupLogic for repeated numbers
		this.lookupCache = {};
		/**
		 * @type { NumberArtifact }
		 */
		this.numberArtifact = {};
		this.insertedNodes = [];
		this.deptDestinationNumbers = {};

		this.thirdPartyTrackingConfig =
			window.DDC.callTrackingSettings.thirdPartyPhoneTrackingConfiguration;

		this.metrics = {
			requestsMade: 0,
			numbersSwapped: 0
		};

		// Work around the issue where we see values like this on some live sites: 677078&tcdadid=354042009735
		// Instead of this: 677078

		if (this.tcdcmpid && this.tcdcmpid.indexOf('&') > -1) {
			this.newrelic.setCustomAttribute('tcdCampaignIdInvalid', this.tcdcmpid);
			this.tcdcmpid = this.tcdcmpid.substring(0, this.tcdcmpid.indexOf('&'));
		}

		if (this.newrelic && this.tcdcmpid) {
			this.newrelic.setCustomAttribute('tcdCampaignId', this.tcdcmpid);
		}

		let accountsToLoad = [];
		const overrideAccountIds =
			window.DDC.callTrackingSettings.callTrackingAccountIdsOverride || [];
		if (this.thirdPartyTrackingConfig) {
			// third party tracking does not support multiple accounts, only load current siteId
			accountsToLoad = [window.DDC.siteSettings.accountId];
			this.loggingContext = `PHONESWAPPING:THIRD PARTY (${this.thirdPartyTrackingConfig.name})`;
		} else {
			// remove duplicates and store list of ddcAccountIds to load numbers for
			const childAccountIds = window.DDC.siteSettings.childAccountIds || [];
			accountsToLoad =
				overrideAccountIds.length < 1
					? [window.DDC.siteSettings.accountId, ...childAccountIds]
					: [window.DDC.siteSettings.accountId, ...overrideAccountIds];
			this.loggingContext = 'PHONESWAPPING:DDC';
		}
		this.accountArtifactsToLoad = accountsToLoad.filter((elem, pos, arr) => {
			return arr.indexOf(elem) === pos;
		});

		this.platform = window.DDC.client.isMobile
			? 'DDC_MOBILE'
			: 'DDC_PRIMARY_WEBSITE';
		this.log(`Phone Swapping platform: ${this.platform}`);
		// These departments (Company Phone 2/3) are referred to as CUSTOM1/2 reference ids in cms #facepalm
		this.DDC_DEPT_CONVENTION = {
			'COMPANY PHONE 2': 'CUSTOM1',
			'COMPANY PHONE 3': 'CUSTOM2'
		};
		// Websites use CUSTOM1/2 as reference numbers in cms, so they need to be translated for campagins #facepalm
		this.DDC_ONSITE_CONVENTION = { CUSTOM1: 'SERVICE', CUSTOM2: 'PARTS' };
		this.FETCH_ERROR_MESSAGE = 'Error fetching phone numbers:';

		this.logIfFound(this.tcdcmpid, `Found TCD campaign ID: ${this.tcdcmpid}`);
		this.logIfFound(this.ddcref, `Found ddcref campaign ID: ${this.ddcref}`);
		this.logIfFound(
			this.artifactOverride,
			`Phone Swapping Artifact Override JSON: ${this.artifactOverride}`
		);
		this.processedNodes = new WeakSet();
	}

	init(startTime) {
		if (this.numberArtifact) {
			const t0 = performance.now();
			this.mergePhoneArtifacts();
			this.domTraverse(this.root, this.swapNumbers);
			this.domTraverse(this.head, this.swapNumbers);
			const t1 = performance.now();
			const domParseTime = (t1 - t0).toFixed(2);
			const totalTimeElapsed = (t1 - startTime).toFixed(2);

			this.trackPerformance('DOM PARSE', domParseTime);
			this.trackPerformance('TOTAL TIME ELAPSED', totalTimeElapsed);

			if (document.readyState === 'loading') {
				window.addEventListener('DOMContentLoaded', () => this.onReady());
			} else {
				this.onReady();
			}

			// This is to update CTAE button on SRP with the tracking numbers
			window.addEventListener('wsInvListingShown', (e) => {
				const uuid = e.detail.id;
				const element = document.querySelector(`[data-uuid='${uuid}']`);
				if (element) {
					this.domTraverse(element, this.swapNumbers);
				}
			});
		}
	}

	onReady() {
		// Initialize aiIdExchange functionality
		initAiIdExchange();

		this.domTraverse(this.root, this.swapNumbers);
		this.domTraverse(this.head, this.swapNumbers);
		// Observe DOM changes and parse the inserted nodes
		this.observeDOM(document.body, this.observedNodeTraverse.bind(this, this));
	}

	initIfReady(startTime) {
		const loadedCount = Object.values(this.loadedAccountArtifacts).filter(
			(v) => v
		).length;
		if (loadedCount > 0) {
			this.trackPerformance(
				'TOTAL ARTIFACT REQUESTS',
				(performance.now() - startTime).toFixed(2)
			);
			this.init(startTime);
		} else {
			this.log('No phone artifacts loaded. Skipping DOM parse.');
		}
	}

	observeDOM(obj, callback) {
		const MutationObserver =
			window.MutationObserver || window.WebKitMutationObserver;

		if (!obj || obj.nodeType !== 1) return; // validation

		if (MutationObserver) {
			// define a new observer
			const obs = new MutationObserver((mutations) => {
				const addedNodes = [];
				mutations.forEach((mutation) => {
					if (mutation.addedNodes.length) {
						addedNodes.push(...mutation.addedNodes);
					}
				});
				if (addedNodes.length) {
					callback(addedNodes);
				}
			});

			// have the observer observe foo for changes in children
			obs.observe(obj, { childList: true, subtree: true });
		} else if (window.addEventListener) {
			obj.addEventListener('DOMNodeInserted', callback, false);
			obj.addEventListener('DOMNodeRemoved', callback, false);
		}
	}

	observedNodeTraverse(phoneSwapper, nodes) {
		nodes.forEach((node) => {
			if (node.nodeType === 1) {
				phoneSwapper.swapPhoneOnNode(node);
				if (node.childNodes.length) {
					this.observedNodeTraverse(phoneSwapper, Array.from(node.childNodes));
				}
			}
		});
	}

	domTraverse(node, func) {
		// eslint-disable-next-line no-param-reassign
		if (!node) node = document.body;
		func.call(this, node);
		// If the artifact is ready before the document.body is ready, then this will avoid a null pointer
		if (node) {
			// eslint-disable-next-line no-param-reassign
			node = node.firstChild;
		}
		while (node) {
			// exclude all external script source attributes, all css links and inline styles
			const isExternalScript =
				node.tagName === 'SCRIPT' &&
				node.getAttribute &&
				node.getAttribute('src');
			const isStyle = node.tagName === 'LINK' || node.tagName === 'STYLE';

			if (!isExternalScript && !isStyle) {
				this.domTraverse(node, func);
			}
			// eslint-disable-next-line no-param-reassign
			node = node.nextSibling;
		}
	}

	/**
	 * replace all standard non number phone characters (, ), -, +, . and return just the string of numbers
	 * @param number
	 */
	cleanNumber(number) {
		return number.replace(this.cleanRegex, '');
	}

	/**
	 * Search children of target and replace all numbers associated with a tracking campaign or department
	 * @param target - parent el to look for numbers in ( defaults to this.root )
	 */
	swapNumbers(node) {
		const swapTarget = node || this.root;

		if (this.numberArtifact) {
			this.swapPhoneOnNode(swapTarget);
		} else if (this.waitCount < this.waitLimit) {
			// eslint-disable-next-line no-plusplus
			this.waitCount++;
			setTimeout(() => this.swapNumbers(node), 100);
		}
	}

	/**
	 * This is the function that runs through the whole node system
	 * in the dom and swaps the numbers, meta tags and links
	 */
	swapPhoneOnNode(node) {
		if (!node || this.processedNodes.has(node)) return;
		this.processedNodes.add(node);
		if (node) {
			let dept;
			let acctId;

			if (node.hasAttribute && node.hasAttribute('data-phone-ref')) {
				dept = node.getAttribute('data-phone-ref');
				acctId = node.getAttribute('data-account-ref');
			}

			if (node.dataset) {
				this.swapDataAttributeNumber(node, dept, acctId);
			}

			switch (node.nodeName) {
				case '#text':
					this.swapTextNodeNumber(node, dept, acctId);
					break;
				case 'A':
					this.swapLinkNodeNumber(node, dept, acctId);
					break;
				case 'META':
					this.swapMetaNodeNumber(node, dept, acctId);
					break;
				default:
				// do nothing
			}
		}
	}

	swapDataAttributeNumber(node, dept, acctId) {
		if (node.dataset) {
			let phoneDept = dept;
			let phoneAcctId = acctId;

			if (!phoneDept && !phoneAcctId) {
				const replacementMeta = this.getReplacementMetaDataFromNode(node);
				[phoneDept, phoneAcctId] = replacementMeta;
			}

			Object.keys(node.dataset).forEach((attr) => {
				const value = node.dataset[attr];
				const phoneRxResults = this.compiledPhoneRegEx.exec(value);
				if (phoneRxResults && phoneRxResults[0] && this.validPhoneAttr(attr)) {
					const fullPhoneString = phoneRxResults[0];
					const phoneNumber = phoneRxResults[1];
					const phoneNumberMeta = phoneRxResults[2];

					if (
						phoneNumberMeta &&
						this.phoneMetaParamRegex.test(phoneNumberMeta)
					) {
						const parsedMeta = this.phoneMetaParamRegex.exec(phoneNumberMeta);
						[, phoneDept, phoneAcctId] = parsedMeta;
					}

					const replacementNumber = this.getNumber(
						phoneNumber,
						phoneDept,
						phoneAcctId
					);
					if (replacementNumber) {
						const formattedNumber =
							this.formatReplacementNumber(replacementNumber);
						this.logNumberSwap(
							'DATA',
							fullPhoneString,
							phoneNumber,
							phoneDept,
							phoneAcctId,
							formattedNumber
						);
						// eslint-disable-next-line no-param-reassign
						node.dataset[attr] = value.replace(
							fullPhoneString,
							formattedNumber
						);
					} else {
						// eslint-disable-next-line no-param-reassign
						node.dataset[attr] = value.replace(fullPhoneString, phoneNumber);
					}
				}
			});
		}
	}

	swapMetaNodeNumber(node, dept, acctId) {
		if (node && node.content && node.content.length > 0) {
			let results;
			const phoneDept = dept;
			const phoneAcctId = acctId;
			let value = node.content;
			// eslint-disable-next-line no-cond-assign
			while ((results = this.compiledPhoneRegEx.exec(node.content)) !== null) {
				const fullPhoneString = results[0];
				const phoneNumber = results[1];
				const replacementNumber = this.getNumber(phoneNumber, phoneDept);
				if (replacementNumber) {
					const formattedNumber =
						this.formatReplacementNumber(replacementNumber);
					this.logNumberSwap(
						'META',
						fullPhoneString,
						phoneNumber,
						phoneDept,
						phoneAcctId,
						formattedNumber
					);
					value = value.replace(
						new RegExp(fullPhoneString, 'g'),
						formattedNumber
					);
				}
			}
			// eslint-disable-next-line no-param-reassign
			node.content = value;
		}
	}

	swapTextNodeNumber(node, dept, acctId) {
		if (node && node.nodeValue && node.nodeValue.length > 0) {
			let results;

			// eslint-disable-next-line no-cond-assign
			while ((results = this.compiledPhoneRegEx.exec(node.data)) !== null) {
				let phoneDept = dept;
				let phoneAcctId = acctId;

				if (!phoneDept && !phoneAcctId) {
					const replacementMeta = this.getReplacementMetaDataFromNode(node);
					[phoneDept, phoneAcctId] = replacementMeta;
				}

				const fullPhoneString = results[0];
				const phoneNumber = results[1];
				const phoneNumberMeta = results[2];

				if (phoneNumberMeta && this.phoneMetaParamRegex.test(phoneNumberMeta)) {
					const parsedMeta = this.phoneMetaParamRegex.exec(phoneNumberMeta);
					[, phoneDept, phoneAcctId] = parsedMeta;
				}

				const swapPhoneNumber = this.getNumber(
					phoneNumber,
					phoneDept,
					phoneAcctId
				);

				if (swapPhoneNumber) {
					const formattedNumber = this.formatReplacementNumber(swapPhoneNumber);
					this.logNumberSwap(
						'TEXT',
						fullPhoneString,
						phoneNumber,
						phoneDept,
						phoneAcctId,
						formattedNumber
					);
					// eslint-disable-next-line no-param-reassign
					node.nodeValue = node.nodeValue.replace(
						fullPhoneString,
						formattedNumber
					);

					const { parentNode } = node;
					if (parentNode && parentNode.hasAttribute('data-phone-ref')) {
						if (parentNode.replaceWith) {
							parentNode.replaceWith(node);
						} else {
							// replaceWith not supported in all browsers, fall back to old parentNode chain.replaceChild
							parentNode.parentNode.replaceChild(node, parentNode);
						}
					}
				}
			}
		}
	}

	swapLinkNodeNumber(node, dept, acctId) {
		const results = this.compiledLinkPhoneRegex.exec(node.href);
		if (results && results[0]) {
			let phoneDept = dept;
			let phoneAcctId = acctId;

			const fullPhoneString = results[0];
			const phoneNumber = results[1];
			const phoneNumberMeta = results[2];

			if (!phoneDept && !phoneAcctId) {
				const replacementMeta = this.getReplacementMetaDataFromNode(node);
				[phoneDept, phoneAcctId] = replacementMeta;
			}

			if (phoneNumberMeta && this.phoneMetaParamRegex.test(phoneNumberMeta)) {
				const parsedMeta = this.phoneMetaParamRegex.exec(phoneNumberMeta);
				[, phoneDept, phoneAcctId] = parsedMeta;
			}

			const swapPhoneNumber = this.getNumber(
				phoneNumber,
				phoneDept,
				phoneAcctId
			);
			if (swapPhoneNumber) {
				this.logNumberSwap(
					'LINK',
					fullPhoneString,
					phoneNumber,
					phoneDept,
					phoneAcctId,
					swapPhoneNumber
				);
				// eslint-disable-next-line no-param-reassign
				node.href = `tel:${swapPhoneNumber.replace(/\s*/gi, '')}`;
			} else {
				// eslint-disable-next-line no-param-reassign
				node.href = `tel:${phoneNumber.replace(/\s*/gi, '')}`;
				// adding the data attribute back to dom node so that we don't lose the information to make a valide getNumber call
				// this node may examine again due to the dom observer
				node.setAttribute('data-phone-ref', phoneDept);
				node.setAttribute('data-account-ref', phoneAcctId);
			}
		}
	}

	getCategorizedTrackingData(
		replacementNumberArtifacts,
		platform,
		category,
		trackingId
	) {
		return replacementNumberArtifacts.find((trackingData) => {
			if (!trackingData.isTrackingNumber) {
				return false;
			}
			if (!category) {
				return trackingData.trackingCategory === platform;
			} else if (trackingId) {
				return (
					trackingData.trackingCategory === category &&
					trackingData.trackingId === trackingId
				);
			} else {
				return trackingData.trackingCategory === category;
			}
		});
	}

	validPhoneAttr(attr) {
		return !this.excludedAttributes.includes(attr);
	}

	/**
	 *  Short circuit the department look up process if there is no tcdcmpid
	 *  This method is trying to find the right department based of ddc convention.
	 * @departments The departments that single destination number is mapped to.
	 * @platform
	 */
	getDeptForDDCCategory(departments, platform) {
		if (departments.indexOf('COMPANY PHONE 2') > -1) {
			if (platform === 'DDC_MOBILE') {
				return 'SERVICE';
			} else if (platform === 'DDC_PRIMARY_WEBSITE') {
				return 'CUSTOM1';
			}
		} else if (departments.indexOf('COMPANY PHONE 3') > -1) {
			if (platform === 'DDC_MOBILE') {
				return 'PARTS';
			} else if (platform === 'DDC_PRIMARY_WEBSITE') {
				return 'CUSTOM2';
			}
		} else if (departments.length === 1) {
			return departments[0];
		}
		return undefined;
	}

	/**
	 * helper method find numberArtifacts by account id and dept from global this.numberArtifact
	 * this.numberArtifact = {
	 *       maxwellfordfd:{
	 *           SALSE: [{number: "8665540517", isTrackingNumber: true, trackingCategory: "DDC_PRIMARY_WEBSITE"},...],
	 *           PARTS: [...],
	 *           ...numberArtifacts
	 *       },
	 *       shamaleyfordfd : {...},
	 *       ...
	 * }
	 * @param numberArtifact
	 * @param acctId
	 * @param lookupDept
	 * @returns {*}
	 */
	getReplacementNumberArtifacts(numberArtifact, acctId, lookupDept) {
		let replacementArtifactsForDept = [];
		if (numberArtifact[acctId]) {
			if (numberArtifact[acctId][lookupDept]) {
				replacementArtifactsForDept = [...numberArtifact[acctId][lookupDept]];
			}

			if (
				this.DDC_ONSITE_CONVENTION[lookupDept] &&
				numberArtifact[acctId][this.DDC_ONSITE_CONVENTION[lookupDept]]
			) {
				replacementArtifactsForDept = [
					...replacementArtifactsForDept,
					...numberArtifact[acctId][this.DDC_ONSITE_CONVENTION[lookupDept]]
				];
			}

			if (numberArtifact[acctId][this.DEPT_WILD_CARD]) {
				// if there are wildcard department numbers, add them in. This usually means a third party has supplied department agnostic phone numbers
				replacementArtifactsForDept = [
					...replacementArtifactsForDept,
					...numberArtifact[acctId][this.DEPT_WILD_CARD]
				];
			}
		}

		return replacementArtifactsForDept;
	}

	/**
	 * figure out which account id and department the phone number on the page belongs to.
	 * this.deptDestinationNumbers = {
	 *       4053025990: {'maxwellfordfd':['SALSE, SERVICES']},
	 *       9136711313: {'maxwellfordfd':['PARTS']},
	 *       9156294599: {'shamaleyfordfd':['SALSE']},
	 *        .....
	 *  }

	 * @deptDestinationNumbers destination number and department map
	 * @phoneNumberOnPage The phone number on the page
	 */
	phoneNumberOnPageToAccountDept(deptDestinationNumbers, phoneNumberOnPage) {
		let matchedDept = null;
		let matchedAcctId = null;
		const destinationNumberAccountDepts =
			deptDestinationNumbers[phoneNumberOnPage] || {};
		// TODO handle the case destination number was shared by two accounts
		// eslint-disable-next-line no-restricted-syntax
		for (const acctId of Object.keys(destinationNumberAccountDepts)) {
			const accountDepartments = destinationNumberAccountDepts[acctId];
			if (!this.tcdcmpid) {
				const lookupDept = this.getDeptForDDCCategory(
					accountDepartments,
					this.platform
				);
				const replacementNumberArtifacts = this.getReplacementNumberArtifacts(
					this.numberArtifact,
					acctId,
					lookupDept
				);
				if (replacementNumberArtifacts) {
					matchedDept = lookupDept;
					matchedAcctId = acctId;
					break;
				}

				// eslint-disable-next-line no-restricted-syntax
				for (const dept of accountDepartments) {
					let accountLookupDept = dept;
					const ddcDept =
						this.DDC_DEPT_CONVENTION[dept] ||
						this.DDC_DEPT_CONVENTION[dept.toUpperCase()];
					accountLookupDept = ddcDept || dept;
					const accountReplacementNumberArtifacts =
						this.getReplacementNumberArtifacts(
							this.numberArtifact,
							acctId,
							accountLookupDept
						);
					if (accountReplacementNumberArtifacts) {
						matchedDept = accountLookupDept;
						matchedAcctId = acctId;
						break;
					}
				}
			} else {
				// eslint-disable-next-line no-restricted-syntax
				for (const dept of accountDepartments) {
					const replacementNumberArtifacts = this.getReplacementNumberArtifacts(
						this.numberArtifact,
						acctId,
						dept
					);
					if (replacementNumberArtifacts) {
						matchedDept = dept;
						matchedAcctId = acctId;
						break;
					}
				}
			}
		}

		return [matchedAcctId, matchedDept];
	}

	/**
	 * Serves the functions inside @swapPhoneOnNode and gets all the data to be replaced in the nodes
	 */
	getReplacementMetaDataFromNode(node) {
		if (!node) {
			return [];
		}

		let lookupNode = node;
		let phoneDept;
		let phoneAcctId;

		let traverseCap = 0;
		let hasMeta =
			lookupNode.hasAttribute && lookupNode.hasAttribute('data-phone-ref');

		while (
			!hasMeta &&
			lookupNode &&
			lookupNode !== document.body &&
			traverseCap < 3
		) {
			lookupNode = lookupNode.parentNode;

			hasMeta =
				lookupNode &&
				lookupNode.hasAttribute &&
				lookupNode.hasAttribute('data-phone-ref');
			traverseCap += 1;
		}

		if (lookupNode && lookupNode.getAttribute) {
			phoneDept = lookupNode.getAttribute('data-phone-ref');
			phoneAcctId = lookupNode.getAttribute('data-account-ref');
		}

		return [phoneDept, phoneAcctId];
	}

	/**
	 *  Find the list of tracking data based of ddcAccountId and department the phone is mapped to
	 * @replacementNumberArtifacts The list of tracking data for certain department
	 * @matchedAccountAndDept The account and department the phone on page map to
	 */
	getReplacementTrackingData(
		replacementNumberArtifacts,
		matchedAccountAndDept
	) {
		let replacementTrackingData = null;
		const matchedDept = matchedAccountAndDept[1];
		if (replacementNumberArtifacts) {
			// tcdcmpid and ddcref are trackingId sources, so check for records associated to the campaigns
			// tcd campaigns take precedence over ddcref campaigns
			if (this.tcdcmpid) {
				// This is a special case for advertising.
				// They may supply a mobile specific number for Sales on a TCD campaign
				if (this.platform === 'DDC_MOBILE' && matchedDept === 'SALES') {
					replacementTrackingData = this.getCategorizedTrackingData(
						replacementNumberArtifacts,
						this.platform,
						'MOBILE_AD',
						this.tcdcmpid
					);
				}
				if (!replacementTrackingData) {
					replacementTrackingData = this.getCategorizedTrackingData(
						replacementNumberArtifacts,
						this.platform,
						'TCD',
						this.tcdcmpid
					);
				}
			}
			if (!replacementTrackingData && this.ddcref) {
				replacementTrackingData = this.getCategorizedTrackingData(
					replacementNumberArtifacts,
					this.platform,
					'CAMPAIGN',
					this.ddcref
				);
			}
			// Some phone numbers aren't tied to a campaign so there may not be any results from the logic above
			if (!replacementTrackingData) {
				replacementTrackingData = this.getCategorizedTrackingData(
					replacementNumberArtifacts,
					this.platform,
					this.platform
				);
			}
		}

		return replacementTrackingData;
	}

	/**
	 * wrapper method that uses lookupCache to find number without call getNumbers() if possible.
	 * Also returns null if number has already been swapped
	 * - optional - if acctId/dept supplied used to lookup the phone number inside of the phone swapping artifact supplied
	 * @param lookupNumber
	 * @param dept - optional
	 * @param acctId - optional
	 * @returns {*}
	 */
	getNumber(lookupNumber, dept, acctId) {
		if (!lookupNumber || !acctId) {
			return false;
		}

		const currentPlatform =
			this.platform === 'DDC_MOBILE' ? 'MOBILE_AD' : 'TCD';
		const cleanLookupNumber = this.cleanNumber(lookupNumber).replace(
			'tel:',
			''
		);
		this.log(
			`Clean lookup number from cleanNumber: ${cleanLookupNumber} and account: ${acctId}`
		);

		let lookupKey = '';
		if (dept && acctId) {
			lookupKey = `${cleanLookupNumber}_${dept}_${acctId}`;
		} else {
			return null;
		}

		this.log(
			`Lookup key: ${lookupKey} and current cache, checking index for already swapped: ` +
				`${Object.values(this.lookupCache).indexOf(lookupKey)}`,
			this.lookupCache
		);
		const isAlreadySwappedNumber =
			Object.values(this.lookupCache).indexOf(lookupKey) !== -1;
		this.log(`Account ${acctId}, already swapped: ${isAlreadySwappedNumber}`);

		if (isAlreadySwappedNumber) {
			return null;
		}
		if (!this.lookupCache[lookupKey]) {
			this.lookupCache[lookupKey] = this.lookupNumber(
				cleanLookupNumber,
				dept,
				acctId
			);
			// Validate tracking ID and Accounts fetch call worked correctly
			if (this.tcdcmpid && !!Object.keys(this.allAccounts).length) {
				// Separates the current account we want to check from All the accounts we get from the service
				const currentAccounts = this.allAccounts.filter(
					(acct) => acct.account === acctId
				);
				this.log(
					`For ${acctId} found ${currentAccounts.length > 0 ? currentAccounts.length : 0} results`
				);
				// If there is more than one account loop through them and filter the ones that match the tracking ID and the correct Department
				currentAccounts.forEach((currAcct) => {
					// Filter the phone numbers with the tracking ID being used for the advertisement campaign, right department and either mobile or desktop
					const phoneSwapArray = currAcct.phones.filter(
						(phoneProperties) =>
							phoneProperties.trackingId === this.tcdcmpid &&
							phoneProperties.profitCenter === dept &&
							phoneProperties.trackingCategory === currentPlatform
					);
					if (phoneSwapArray.length) {
						this.log(
							`Results in phone swap array matching ${this.tcdcmpid} ${dept}`,
							phoneSwapArray
						);
						this.lookupCache[lookupKey] = phoneSwapArray[0].number;
					} else {
						const topLevelAccount = this.allAccounts.filter(
							(acct) =>
								acct.account ===
								window.DDC.siteProperties.topLevelPhoneSwappingAccount
						);
						this.log(
							`Using top level account for number: ${JSON.stringify(topLevelAccount)}`
						);
						if (topLevelAccount.length) {
							// Need to validate Tracking ID in case the wrong number gets passed
							const filterTopLevelAccount = topLevelAccount[0].phones.filter(
								(phoneProperties) =>
									phoneProperties.trackingId === this.tcdcmpid &&
									phoneProperties.profitCenter === dept &&
									phoneProperties.trackingCategory === currentPlatform
							);
							if (filterTopLevelAccount.length) {
								this.log(
									`Top level account after filters: ${JSON.stringify(filterTopLevelAccount)}`
								);
								this.lookupCache[lookupKey] = filterTopLevelAccount[0].number;
							}
						}
					}
				});
			}
		}
		return this.lookupCache[lookupKey];
	}

	/**
	 * Main replace phone number look up logic
	 *  set up a template/algorithm for look up replaced number.
	 * @phoneNumberOnPage phone number on Page
	 */
	lookupNumber(phoneNumberOnPage, dept, acctId) {
		let countryCode = null;
		if (phoneNumberOnPage.length === 11) {
			// eslint-disable-next-line no-param-reassign
			[countryCode, ...phoneNumberOnPage] = phoneNumberOnPage;
		}

		const matchedAccountAndDept = [acctId, dept];
		if (!matchedAccountAndDept[0] || !matchedAccountAndDept[1]) {
			const destinationNumber = this.deptDestinationNumbers[phoneNumberOnPage];

			if (destinationNumber) {
				const acctDeptFromLookup = this.phoneNumberOnPageToAccountDept(
					this.deptDestinationNumbers,
					phoneNumberOnPage
				);

				matchedAccountAndDept[0] =
					matchedAccountAndDept[0] || acctDeptFromLookup[0];
				matchedAccountAndDept[1] =
					matchedAccountAndDept[1] || acctDeptFromLookup[1];
			}
		}

		// find the artifacts this account and department map to.
		const replacementNumberArtifacts = this.getReplacementNumberArtifacts(
			this.numberArtifact,
			matchedAccountAndDept[0],
			matchedAccountAndDept[1]
		);

		// find the replaced tracking data based off platform, tracking id and ddcref
		const replacementTrackingData = this.getReplacementTrackingData(
			replacementNumberArtifacts,
			matchedAccountAndDept
		);

		const replacementNumber = replacementTrackingData
			? replacementTrackingData.number
			: null;
		if (replacementNumber && countryCode) {
			return `${countryCode} ${replacementNumber}`;
		}
		this.log(`Replacement number from lookupNumber: ${replacementNumber}`);
		return replacementNumber;
	}

	formatReplacementNumber(number) {
		let formattedNumber;
		let replacementFormat = this.defaultReplacementFormat;
		switch (window?.DDC?.siteProperties?.phoneNumberFormat) {
			case 'parentheses':
				replacementFormat = this.parensReplacementFormat;
				break;
			case 'dot':
				replacementFormat = this.dotReplacementFormat;
				break;
			default:
				replacementFormat = this.defaultReplacementFormat;
		}
		// if the number has no formatting, give it the default formatting
		if (number && number === this.cleanNumber(number)) {
			formattedNumber = number.replace(
				this.defaultCleanCapturingRegEx,
				replacementFormat
			);
		}
		return formattedNumber || number; // return original number if re-formatted number doesn't work
	}

	/**
	 * Fetch the phone artifact and process it to initialize prior to swapping
	 *
	 * @param ddcAccountId
	 * @param startTime - used in tracking if passed in, defaults to performance.now() of the loadNumbers call
	 */
	loadNumbers(ddcAccountId, startTime) {
		const t0 = startTime || performance.now();
		if (this.artifactOverride) {
			const responseObj = JSON.parse(this.artifactOverride);
			this.fillDeptToDestMap(ddcAccountId, responseObj.phones);
			this.log(
				`Using Artifact Override: ${(performance.now() - t0).toFixed(2)} ms.`
			);
			this.init(t0);
		} else if (this.thirdPartyTrackingConfig) {
			const { url, apiKey, username, password, accountId } =
				this.thirdPartyTrackingConfig;
			const requestStartTime = performance.now();

			this.metrics.requestsMade += 1;

			const xmlhttp = new XMLHttpRequest();
			xmlhttp.open('POST', url, true);
			xmlhttp.onreadystatechange = function onreadystatechange() {
				if (xmlhttp.readyState === 4) {
					const { status } = xmlhttp;
					if (status === 200) {
						const artifactResponseTime = (
							performance.now() - requestStartTime
						).toFixed(2);
						if (artifactResponseTime > 500) {
							this.trackPerformance(
								'ARTIFACT REQUEST SLOW',
								artifactResponseTime
							);
						}

						this.loadedAccountArtifacts[ddcAccountId] = true;

						const phones = JSON.parse(xmlhttp.responseText).map(
							(phoneInfo) => ({
								number: phoneInfo.number,
								isTrackingNumber: true,
								trackingCategory: this.platform,
								locationOnSite: this.DEPT_WILD_CARD
							})
						);

						this.fillDeptToDestMap(ddcAccountId, phones, false);
						this.initIfReady(t0);
					} else if (status >= 400 && status < 500) {
						// 400s should fail the load cycle immediately
						this.handleNumberLoadFail.apply(this, [
							ddcAccountId,
							startTime,
							status
						]);
					} else if (status >= 500) {
						// 500s should retry
						this.handleNumberLoadError.apply(this, [
							ddcAccountId,
							startTime,
							status
						]);
					}
					if (this.newrelic) {
						this.newrelic.setCustomAttribute('phoneSwapStatus', status);
					}
				}
			}.bind(this);
			xmlhttp.send(
				this.generateThirdPartyPostBody(
					username,
					password,
					apiKey,
					accountId,
					'jsonString'
				)
			);
		} else {
			const requestStartTime = performance.now();

			this.metrics.requestsMade += 1;

			const xhr = new XMLHttpRequest();
			xhr.open(
				'GET',
				`${window.DDC.callTrackingSettings.callTrackingArtifactBaseUrl}/${ddcAccountId}.json`,
				true
			);
			xhr.onload = () => {
				if (xhr.status === 200) {
					const responseObj = JSON.parse(xhr.responseText);
					this.fillDeptToDestMap(ddcAccountId, responseObj.phones);
					this.loadedAccountArtifacts[ddcAccountId] = true;

					const artifactResponseTime = (
						performance.now() - requestStartTime
					).toFixed(2);
					if (artifactResponseTime > 500) {
						this.trackPerformance(
							'ARTIFACT REQUEST SLOW',
							artifactResponseTime
						);
					}

					this.initIfReady(t0);
				} else {
					// 500s should retry
					this.handleNumberLoadError.apply(this, [
						ddcAccountId,
						startTime,
						xhr.status
					]);
				}
			};
			xhr.onerror = () => {
				if (xhr.status >= 500) {
					// 500s should retry
					this.handleNumberLoadError.apply(this, [
						ddcAccountId,
						startTime,
						xhr.status
					]);
				} else {
					// 400s should fail the load cycle immediately
					this.handleNumberLoadFail.apply(this, [
						ddcAccountId,
						startTime,
						xhr.status
					]);
				}
			};
			xhr.send();
		}
	}

	/**
	 * @param { string[] } accountIds
	 * @param { number? } startTime
	 */
	loadNumbersFromService(accountIds, startTime = performance.now()) {
		const serviceUrl = new URL(window.DDC.phonesUrl);
		serviceUrl.searchParams.append('platform', this.platform);

		if (this.tcdcmpid) {
			serviceUrl.searchParams.append('tcdcmpid', this.tcdcmpid);
		}

		if (this.ddcref) {
			serviceUrl.searchParams.append('ddcref', this.ddcref);
		}

		this.metrics.requestsMade += 1;

		if (this.newrelic) {
			const phoneSwapServiceVersion = window.DDC.phonesUrl.includes(
				'phone-swap-service-v2'
			)
				? 'v2'
				: 'v1';
			this.newrelic.setCustomAttribute(
				'phoneSwapServiceVersion',
				phoneSwapServiceVersion
			);
		}

		// Check if autoId Integration is enabled
		if (
			this.isAutoIdIntegration() &&
			this.waitCountAutoId < this.waitLimitAutoId
		) {
			// Integration is enabled
			if (window.autoid_inited) {
				// For each account Id process the phone numbers through autoID
				// Note: window.phoneNumbers is set by cms-web vcard component
				// @see https://ghe.coxautoinc.com/DDC-WebPlatform/cms-web/blob/master/htdocs/v9/components/vcard/js/v1/component.vm
				// Format: { "phoneNumber": "DEPARTMENT", ... } e.g., {"8888952994":"SALES","8888952993":"CUSTOM1"}
				accountIds.forEach((account) => {
					this.loadNumbersFromAutoId(account, startTime, window.phoneNumbers);
				});
			} else {
				this.waitCountAutoId += 1;
				setTimeout(
					() => this.loadNumbersFromService(accountIds, startTime),
					100
				);
			}
		} else {
			// Fetch account information from phone swapping service
			try {
				const controller = new AbortController();
				const timeoutId = setTimeout(() => controller.abort(), 3000);
				fetch(serviceUrl, {
					method: 'POST',
					headers: {
						'Content-Type': 'application/json'
					},
					body: JSON.stringify(accountIds),
					signal: controller.signal
				})
					.then((response) => {
						clearTimeout(timeoutId);
						return response.json();
					})
					.then((/** @type { AccountInfo[] } */ accounts) => {
						if (Array.isArray(accounts)) {
							// Set the allAccounts list to the account information
							this.allAccounts = accounts;
							// For each account process the phone numbers through phone swapping
							accounts.forEach((account) => {
								// get the accountId and phoneNumberArtifacts out of the account response
								const { account: accountId, phones } = account;

								// Build a map of the phoneNumberArtifacts
								this.fillDeptToDestMap(accountId, phones);
								this.loadedAccountArtifacts[accountId] = true;
							});

							this.initIfReady(startTime);
						} else {
							this.log(this.FETCH_ERROR_MESSAGE, 'Response is not an array');
						}
					})
					.catch((err) => {
						this.log(this.FETCH_ERROR_MESSAGE, err);
					});
			} catch (err) {
				this.log(this.FETCH_ERROR_MESSAGE, err);
			}
		}
	}

	isAutoIdIntegration() {
		return window.DDC.enableCallsourcePhoneSwapping && !!window.aiGetNumbers;
	}

	/**
	 * Fetch NumberArtifacts from AutoId and then initialize/swap them
	 *
	 * @param { string } accountId
	 * @param { PhoneNumber[] } phones
	 * @param { number } startTime
	 */
	async loadNumbersFromAutoId(accountId, startTime, phones = {}) {
		/**
		 * Storage of new AutoId NumberArtifacts
		 * @type { NumberArtifactDepartmentNode[] }
		 */
		const autoIdArtifacts = [];

		/**
		 * @type { CleanPhoneNumber[] }
		 */
		const numbers = Object.keys(phones).map((phone) => this.cleanNumber(phone));

		try {
			// get the numbers from autoId integration
			/** @type { AutoIdResult } */
			const autoIdResult = await window.aiGetNumbers({ numbers });

			// Check if result has the new structure with numbers array
			const numbersData = autoIdResult?.numbers?.[0] || autoIdResult;

			if (!numbersData || Object.keys(numbersData).length === 0) {
				// If aiGetNumbers did not return any results
				this.handleNumberLoadFail(
					accountId,
					startTime,
					'[aiGetNumbers] no results.'
				);
			} else {
				// Process the results from aiGetNumbers
				// For each original number (key) and tracking number (value) pair
				Object.keys(numbersData).forEach((numberOnSite) => {
					// Get the department from the phones object using the original number
					const departmentName = phones[numberOnSite];

					// Only create a phone artifact if we have a department
					if (departmentName) {
						// create a new NumberArtifact with the autoIdResult number
						/**
						 * @type { NumberArtifactDepartmentNode }
						 */
						const newPhoneArtifact = {
							// autoIdResult stores it's phone numbers as numbers.
							// we need to convert it to a string here.
							number: `${numbersData[numberOnSite]}`,
							isTrackingNumber: true,
							department: departmentName,
							locationOnSite: departmentName,
							trackingCategory: this.platform,
							profitCenter: departmentName
						};

						// Push the new NumberArtifact into the autoIdArtifacts list
						autoIdArtifacts.push(newPhoneArtifact);
					}
				});

				// Push all autoIdArtifacts into the allAccounts list
				this.allAccounts.push({
					account: accountId,
					phones: autoIdArtifacts
				});

				// Build a map of the autoIdArtifacts
				this.fillDeptToDestMap(accountId, autoIdArtifacts);

				// Set the artifacts for the account id to loaded and ready for initialization
				this.loadedAccountArtifacts[accountId] = true;

				// Start Initializtion
				this.initIfReady(startTime);
			}
		} catch (error) {
			// If there was an error in the response from aiGetNumbers
			this.handleNumberLoadFail(accountId, startTime, error);
		}
	}

	loadAllNumbers() {
		const startTime = performance.now();

		this.loadedAccountArtifacts = {};

		if (this.thirdPartyTrackingConfig) {
			const ddcAccountId = this.accountArtifactsToLoad[0];
			this.loadNumbers(ddcAccountId, startTime, this.thirdPartyTrackingConfig);
		} else {
			this.loadNumbersFromService.bind(
				this,
				this.accountArtifactsToLoad,
				startTime
			)();
		}
	}

	/**
	 * build a map of the destination numbers by department phone swapping related data from remote.
	 * this map is only used if no contextual data is provided for a number to be swapped
	 * this.deptDestinationNumbers : department and destination number mapping.
	 * @param ddcAccountId
	 * @param phones  the phone objects from artifact belongs to ddcAccountId
	 */
	fillDeptToDestMap(ddcAccountId, phones, disableFillDeptToDestMap = true) {
		// build map to store account's department destination numbers
		phones.forEach((p) => {
			if (disableFillDeptToDestMap === true) {
				if (this.deptDestinationNumbers[this.cleanNumber(p.number)]) {
					if (
						!this.deptDestinationNumbers[this.cleanNumber(p.number)][
							ddcAccountId
						]
					) {
						// TODO this is where two or more child accounts have same department destination number
						// this.log(`${ddcAccountId} and ${Object.keys(this.deptDestinationNumbers[this.cleanNumber(p.number)])}
						// have same destination number ${this.cleanNumber(p.number)}`);
						this.deptDestinationNumbers[this.cleanNumber(p.number)][
							ddcAccountId
						] = [];
					}
					this.deptDestinationNumbers[this.cleanNumber(p.number)][
						ddcAccountId
					].push(p.department && p.department.toUpperCase());
				} else {
					this.deptDestinationNumbers[this.cleanNumber(p.number)] = {
						[ddcAccountId]: []
					};
					this.deptDestinationNumbers[this.cleanNumber(p.number)][
						ddcAccountId
					].push(p.department && p.department.toUpperCase());
				}
			}
			if (p.isTrackingNumber === true) {
				this.fillPhoneArtifact(ddcAccountId, p);
			}
		});
	}

	/**
	 * fill this.numberArtifact with phonenumbers from list by ddcAccountId and locationOnSite
	 * @param ddcAccountId
	 * @param phones  the phone objects from artifact belongs to ddcAccountId
	 */
	fillPhoneArtifact(ddcAccountId, phone) {
		// build map to store account's tracking numbers
		if (this.numberArtifact[ddcAccountId]) {
			this.numberArtifact[ddcAccountId][phone.locationOnSite] =
				this.numberArtifact[ddcAccountId][phone.locationOnSite] || [];
			this.numberArtifact[ddcAccountId][phone.locationOnSite].push(phone);
		} else {
			this.numberArtifact[ddcAccountId] = { [phone.locationOnSite]: [phone] };
		}
	}

	/**
	 * merge all of the numberArtifacts under the window.DDC.siteSettings.accountId
	 * key so that all tracking numbers apply
	 */
	mergePhoneArtifacts() {
		if (
			window.DDC.siteProperties &&
			window.DDC.siteProperties.callTrackingRollupNumbersToAccount === true
		) {
			window.DDC.callTrackingSettings.callTrackingAccountIdsOverride
				.filter((acct) => acct !== window.DDC.siteSettings.accountId)
				.forEach((accountId) => {
					if (this.numberArtifact[accountId] !== undefined) {
						Object.keys(this.numberArtifact[accountId]).forEach((dept) => {
							if (
								this.numberArtifact[window.DDC.siteSettings.accountId] !==
								undefined
							) {
								if (
									this.numberArtifact[window.DDC.siteSettings.accountId][
										dept
									] === undefined
								) {
									this.numberArtifact[window.DDC.siteSettings.accountId][dept] =
										[];
								}
								this.numberArtifact[window.DDC.siteSettings.accountId][dept] = [
									...this.numberArtifact[window.DDC.siteSettings.accountId][
										dept
									].concat(this.numberArtifact[accountId][dept])
								];
							}
						});
					}
				});
		}
	}

	/**
	 * handler for if the load command does not return the desired artifact, Tries up to {errorLimit} count and then fails
	 */
	handleNumberLoadError(acctId, startTime, context) {
		this.errorCount[acctId] = this.errorCount[acctId] || 0;

		this.errorCount[acctId] += 1;
		if (this.errorCount[acctId] < this.errorLimit) {
			const output = `ARTIFACT REQUEST ERROR ${context ? ` - ${context}` : ''}`;
			this.log(output);
			this.trackPerformance(output, (performance.now() - startTime).toFixed(2));
			this.loadNumbers(acctId, startTime);
		} else {
			this.handleNumberLoadFail(acctId, startTime, null, 'error limit');
		}
	}

	handleNumberLoadFail(acctId, startTime, context) {
		const output = `ARTIFACT REQUEST FAILED ${context ? ` - ${context}` : ''}`;
		this.loadedAccountArtifacts[acctId] = false;
		this.initIfReady(startTime);
		this.trackPerformance(output, (performance.now() - startTime).toFixed(2));
		this.log(`NUMBERS NOT SWAPPED FOR ${acctId}`);
	}

	getCookieValue(cookieKey) {
		const cookie = new RegExp(`${cookieKey}=(.*?)(;|$)`).exec(document.cookie);
		return cookie ? cookie[1] : null;
	}

	/**
	 * conditionally log message if object is found
	 * @param obj
	 * @param logText
	 */
	logIfFound(obj, logText) {
		if (obj) {
			this.log(logText);
		}
	}

	log(...args) {
		if (
			window.location.search.indexOf('_phoneswappingdebug=true') > 0 &&
			window.console
		) {
			window.console.log.apply(this, args);
		} else {
			this.log = function log() {};
			return null;
		}
		return undefined;
	}

	logNumberSwap(
		context,
		fullNumberString,
		originalNumber,
		dept,
		acctId,
		newNumber
	) {
		this.metrics.numbersSwapped += 1;

		if (this.newrelic) {
			this.newrelic.setCustomAttribute(
				'phoneNumbersSwapped',
				this.metrics.numbersSwapped
			);
		}

		this.log(`SWAPPING NUMBER ${context}:`);
		this.log(`Found Swappable Content: ${fullNumberString}`);
		const swapMeta = acctId && dept ? `for ${acctId}-${dept}` : ' first found ';

		this.log(`${originalNumber} ${swapMeta} with ${newNumber}`);
	}

	trackPerformance(action, runningTime) {
		this.log(`${action} took ${runningTime}ms.`);
		if (this.newrelic) {
			this.newrelic.addPageAction('phoneSwapping', {
				loggingContext: this.loggingContext,
				action,
				duration: runningTime ? parseFloat(runningTime) : 0
			});
		}
	}

	generateThirdPartyPostBody(
		username,
		password,
		api,
		thirdPartyAccountId,
		format = 'json'
	) {
		const cfg = {
			api,
			username,
			password,
			site_id: parseInt(thirdPartyAccountId, 10),
			external_id: window.DDC.callTrackingSettings.callTrackingSessionId,

			currentPage: document.location.href,
			defaultPage: window.DDC.siteSettings.defaultDomain,
			defaultDomain: window.DDC.siteSettings.defaultDomain,

			cookies: {
				utmz: this.getCookieValue('__utmz'),
				utma: this.getCookieValue('__utma')
			}
		};

		if (document.referrer) {
			cfg.previousPage = encodeURIComponent(document.referrer);
		}

		if (format === 'jsonString') {
			return JSON.stringify(cfg);
		} else if (format === 'json') {
			return cfg;
		}

		return undefined;
	}
}

const phoneSwapping = new PhoneSwapping();
window.phoneSwapping = phoneSwapping;
phoneSwapping?.loadAllNumbers?.();

export { PhoneSwapping, phoneSwapping };
